/**
 * Snapshots panel — drawer body listing flow snapshots with restore/delete
 * For Media Devoted | Created By Ciro Autiero
 *
 * Mounts as a Radix Dialog (drawer-style) so it can sit on top of the Flow
 * Optimizer drawer without z-index fights.
 */

import { FC, useState } from 'react';
import { X } from 'lucide-react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';

import { useFlowSnapshots } from '../hooks';
import SnapshotRow from './SnapshotRow';
import { useSnapshotActions } from './use-snapshot-actions';

export interface SnapshotsPanelProps {
  flowId: string;
  provider?: TrackerId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once a restore landed. */
  onRestored?: () => void;
}

const SnapshotsPanel: FC<SnapshotsPanelProps> = ({
  flowId,
  provider = 'voluum',
  open,
  onOpenChange,
  onRestored,
}) => {
  const list = useFlowSnapshots(open ? flowId : undefined, provider);
  const { onTake, onRestore, onDelete, busy, taking } = useSnapshotActions({
    flowId,
    provider,
    onRestored,
  });
  const [label, setLabel] = useState('');
  const providerName = trackerLabel(provider);

  const snapshots = list.data?.snapshots ?? [];
  const handleCapture = async () => {
    await onTake(label.trim() || undefined);
    setLabel('');
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent width="w-[min(560px,90vw)]">
        <DrawerHeader className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <DrawerTitle>Flow history</DrawerTitle>
            <DrawerDescription>
              Saved {providerName} flow versions: capture a backup, restore, or
              delete manual entries.
            </DrawerDescription>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            aria-label="Close snapshots"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </DrawerHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Optional label"
              className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none"
              aria-label="Snapshot label"
              maxLength={120}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleCapture}
              disabled={busy}
              loading={taking}
            >
              Capture now
            </Button>
          </div>
          {list.isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : snapshots.length === 0 ? (
            <p className="py-8 text-center text-sm italic text-muted-foreground">
              No snapshots yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {snapshots.map((s) => (
                <SnapshotRow
                  key={s.id}
                  snapshot={s}
                  onRestore={onRestore}
                  onDelete={onDelete}
                  busy={busy}
                />
              ))}
            </ul>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default SnapshotsPanel;
